import { Menu } from "@headlessui/react";
import {
  CheckCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  UserMinusIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const IN_MY_VENDORS_TOOLTIP =
  "This vendor is in My Vendors because of an in-platform request.";

function MenuItemSpinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin text-gray-400"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

/**
 * Shared membership menu items — Phase 1.4 state table.
 * Used on My Vendors card/table and Search Vendors card/row.
 */
export default function MyVendorMembershipMenuItems({
  vendor,
  isInMyVendors = false,
  isManual = false,
  isHidden = false,
  onAddToMyVendors,
  onRemoveFromMyVendors,
  onAddToHidden,
  onUnhide,
  isHidePending = false,
  isUnhidePending = false,
}) {
  if (!vendor) return null;

  const stop = (handler) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    handler?.(vendor);
  };

  const items = [];

  if (!isInMyVendors && onAddToMyVendors) {
    items.push(
      <Menu.Item key="add-to-my-vendors" className="flex items-center">
        {({ active }) => (
          <button
            type="button"
            onClick={stop(onAddToMyVendors)}
            className={classNames(
              active ? "bg-gray-50" : "",
              "flex w-full items-center px-3 py-1 text-left text-sm leading-6 text-gray-900"
            )}
          >
            <UserPlusIcon className="h-4 w-4 text-indigo-600" />
            <span className="pl-2">Add to My Vendors</span>
          </button>
        )}
      </Menu.Item>
    );
    return items;
  }

  if (!isInMyVendors) return null;

  if (isManual && !isHidden && onRemoveFromMyVendors) {
    items.push(
      <Menu.Item key="remove-from-my-vendors" className="flex items-center">
        {({ active }) => (
          <button
            type="button"
            onClick={stop(onRemoveFromMyVendors)}
            className={classNames(
              active ? "bg-gray-50" : "",
              "flex w-full items-center px-3 py-1 text-left text-sm leading-6 text-gray-900"
            )}
          >
            <UserMinusIcon className="h-4 w-4" />
            <span className="pl-2">Remove from My Vendors</span>
          </button>
        )}
      </Menu.Item>
    );
  }

  if (!isManual && !isHidden) {
    items.push(
      <Menu.Item key="in-my-vendors-info" disabled className="flex items-center">
        {() => (
          <div
            className="flex w-full cursor-default items-center px-3 py-1 text-sm leading-6 text-gray-400"
            title={IN_MY_VENDORS_TOOLTIP}
          >
            <CheckCircleIcon className="h-4 w-4" />
            <span className="pl-2">In My Vendors</span>
          </div>
        )}
      </Menu.Item>
    );
  }

  if (!isHidden && onAddToHidden) {
    items.push(
      <Menu.Item key="add-to-hidden" disabled={isHidePending} className="flex items-center">
        {({ active }) => (
          <button
            type="button"
            onClick={stop(onAddToHidden)}
            disabled={isHidePending}
            className={classNames(
              active ? "bg-gray-50" : "",
              "flex w-full items-center px-3 py-1 text-left text-sm leading-6 text-gray-900 disabled:cursor-wait disabled:opacity-60"
            )}
          >
            {isHidePending ? <MenuItemSpinner /> : <EyeSlashIcon className="h-4 w-4" />}
            <span className="pl-2">{isHidePending ? "Hiding…" : "Add to Hidden"}</span>
          </button>
        )}
      </Menu.Item>
    );
  }

  if (isHidden && onUnhide) {
    items.push(
      <Menu.Item key="unhide" disabled={isUnhidePending} className="flex items-center">
        {({ active }) => (
          <button
            type="button"
            onClick={stop(onUnhide)}
            disabled={isUnhidePending}
            className={classNames(
              active ? "bg-gray-50" : "",
              "flex w-full items-center px-3 py-1 text-left text-sm leading-6 text-gray-900 disabled:cursor-wait disabled:opacity-60"
            )}
          >
            {isUnhidePending ? <MenuItemSpinner /> : <EyeIcon className="h-4 w-4 text-indigo-600" />}
            <span className="pl-2">{isUnhidePending ? "Unhiding…" : "Unhide"}</span>
          </button>
        )}
      </Menu.Item>
    );
  }

  return items;
}
